import React, { useState, useRef, useMemo } from "react";
import ActivityPlayer, { ActivityPlayerHandle } from "./ActivityPlayer";
import Translation from "../Subtitles/Translation";
import SpeakButton, { SpeakButtonHandle } from "../Buttons/SpeakButton";
import { SpeakActivity, UserVideoWord } from "@shared_types";
import { Button } from "@chakra-ui/react";
import ContinueButton from "../Buttons/ContinueButton";
import { calcWordDisplayIndex } from "../Video/VideoContent";
import PlacementTestSubtitle from "../PlacementTest/PlacementTestSubtitle";
import { getPlatform } from "@/lib/iap";
import { usePhrase } from "../Video/usePhrase";

interface SpeakProps {
  onActivityFinished?: () => void;
  onTrackListeningTime: (time: number) => void;
  onSkip?: () => void;
  speakActivity: SpeakActivity;
}

const Speak: React.FC<SpeakProps> = ({
  onActivityFinished,
  onTrackListeningTime,
  onSkip,
  speakActivity,
}) => {
  const [currentTime, setCurrentTime] = useState(speakActivity.start);
  const [selectedWord, setSelectedWord] = useState<UserVideoWord | null>(null);
  const [recordingState, setRecordingState] = useState<
    "idle" | "recording" | "recorded"
  >("idle");

  // Add phrase selection state
  const [hasPhraseSelection, setHasPhraseSelection] = useState(false);
  const [phraseStartWordId, setPhraseStartWordId] = useState<
    number | undefined
  >(undefined);
  const [phraseEndWordId, setPhraseEndWordId] = useState<number | undefined>(
    undefined
  );

  const videoRef = useRef<ActivityPlayerHandle>(null);
  const speakButtonRef = useRef<SpeakButtonHandle>(null);
  const pauseHandleTimeUpdate = useRef(false);
  const platform = getPlatform();

  // Calculate phrase string for translation with useMemo for performance
  const selectedPhraseString = useMemo(() => {
    if (phraseStartWordId === undefined || phraseEndWordId === undefined) {
      return "";
    }

    const firstWordIdx = speakActivity.userVideoWords.findIndex(
      (word) => word.videoWordId === phraseStartWordId
    );
    const lastWordIdx = speakActivity.userVideoWords.findIndex(
      (word) => word.videoWordId === phraseEndWordId
    );
    const leftMostWordIdx = Math.min(firstWordIdx, lastWordIdx);
    const rightMostWordIdx = Math.max(firstWordIdx, lastWordIdx);

    return speakActivity.userVideoWords
      .slice(leftMostWordIdx, rightMostWordIdx + 1)
      .map((word) => word.displayText)
      .join(" ");
  }, [phraseStartWordId, phraseEndWordId, speakActivity.userVideoWords]);

  // Add usePhrase hook for phrase translation
  const {
    phraseTranslation,
    isLoading: isPhraseLoading,
    isError,
  } = usePhrase(phraseStartWordId, phraseEndWordId, selectedPhraseString);

  const handleTimeUpdate = (time: number) => {
    if (!pauseHandleTimeUpdate.current) {
      setCurrentTime(time);
    }
  };

  const handleWordClick = (word: UserVideoWord) => {
    pauseHandleTimeUpdate.current = true;
    setSelectedWord(word);
    setCurrentTime(word.start);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.seekTo(word.start);
    }
    // Clear phrase selection when clicking a word
    if (hasPhraseSelection) {
      clearPhraseSelection();
    }
  };

  const handleSkip = () => {
    if (speakButtonRef.current) {
      speakButtonRef.current.pauseAudio();
      speakButtonRef.current.stopRecording();
    }
    if (onSkip) {
      onSkip();
    }
  };

  const handlePlay = () => {
    pauseHandleTimeUpdate.current = false;
  };

  const handleContinue = () => {
    if (speakButtonRef.current) {
      speakButtonRef.current.pauseAudio();
      speakButtonRef.current.stopRecording();
    }
    if (onActivityFinished) {
      onActivityFinished();
    }
  };

  const handlePlayRecording = () => {
    videoRef.current?.pause();
  };

  const handleReplayVideo = () => {
    pauseHandleTimeUpdate.current = false;
    if (videoRef.current) {
      videoRef.current.seekTo(speakActivity.start);
      videoRef.current.triggerOverlayClick();
    }
  };

  const handleTryAgain = () => {
    speakButtonRef.current?.stopRecording();
    speakButtonRef.current?.resetRecording();
    speakButtonRef.current?.startRecording();
  };

  const handleRecordingStateChange = (
    state: "idle" | "recording" | "recorded"
  ) => {
    setRecordingState(state);
  };

  // Add phrase selection handlers
  const handlePhraseSelectStart = () => {
    setPhraseStartWordId(undefined);
    setPhraseEndWordId(undefined);
    pauseHandleTimeUpdate.current = true;
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handlePhraseSelectDone = (
    startWord: UserVideoWord,
    endWord: UserVideoWord
  ) => {
    // Don't allow length of 1
    if (startWord.videoWordId === endWord.videoWordId) {
      return;
    }
    // Save the word IDs for highlighting
    setPhraseStartWordId(startWord.videoWordId);
    setPhraseEndWordId(endWord.videoWordId);
    // Mark that we have a selection
    setHasPhraseSelection(true);
  };

  const clearPhraseSelection = () => {
    setHasPhraseSelection(false);
    setPhraseStartWordId(undefined);
    setPhraseEndWordId(undefined);
  };

  const highlightIndex = calcWordDisplayIndex(
    currentTime,
    speakActivity.userVideoWords
  );

  return (
    <div className="appPadding flex flex-col items-center justify-between w-full h-full max-w-[500px] mx-auto pt-4 gap-4">
      <div className="w-full flex flex-col justify-start gap-4">
        <div className="w-full text-xl font-bold">Speak this sentence</div>
        <div className="bg-white rounded-xl shadow-md w-full overflow-hidden">
          <ActivityPlayer
            ref={videoRef}
            youtubeId={speakActivity.youtubeId}
            onTimeUpdate={handleTimeUpdate}
            onTrackListeningTime={onTrackListeningTime}
            startTime={speakActivity.start}
            endTime={speakActivity.end}
            onPlay={handlePlay}
          />
        </div>
        <div className="flex flex-col items-center justify-center w-full pt-2 gap-2">
          <div className="w-full flex flex-col items-center rounded-xl bg-white p-4 shadow-sm">
            <PlacementTestSubtitle
              userVideoWords={speakActivity.userVideoWords}
              highlightIndex={highlightIndex}
              onWordClick={handleWordClick}
              onPhraseSelectedDone={handlePhraseSelectDone}
              onPhraseSelectStart={handlePhraseSelectStart}
            />
          </div>
          <div className="w-full flex flex-col items-center rounded-xl bg-white p-4 shadow-sm">
            <Translation
              targetWord={selectedWord?.displayText || ""}
              nativeWord={selectedWord?.definition?.nativeWord || ""}
              phraseTranslation={
                hasPhraseSelection ? phraseTranslation : undefined
              }
              isPhraseLoading={isPhraseLoading}
              isPhraseError={isError}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col w-full items-center justify-start gap-4">
        <SpeakButton
          ref={speakButtonRef}
          onRecordingStarted={() => {
            videoRef.current?.pause();
          }}
          onPlayRecording={handlePlayRecording}
          onReplayVideo={handleReplayVideo}
          onTryAgain={handleTryAgain}
          onStateChange={handleRecordingStateChange}
        />
      </div>

      <div className="flex flex-col w-full items-end justify-end">
        {recordingState === "recorded" ? (
          <div
            className={`flex justify-center items-center w-full ${
              platform === "web" ? "bottom-padding-web" : "pb-6"
            }`}
          >
            <ContinueButton onClick={handleContinue} text="Continue" />
          </div>
        ) : (
          <Button
            variant="ghost"
            width="100%"
            color="var(--color-grey)"
            fontSize="var(--base-font-size)"
            onClick={handleSkip}
            _hover={{ bg: "transparent", color: "var(--color-orange)" }}
          >
            I can't speak now
          </Button>
        )}
      </div>
    </div>
  );
};

export default Speak;
